/**
 * SiteVisitModal — buyer books a site visit with calendar + time slot.
 * Requires buyer authentication; unauthenticated users are redirected to register.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Calendar, Clock, User, Phone, MessageSquare, ShieldCheck, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TIME_SLOTS = [
    "09:00 AM - 11:00 AM",
    "11:00 AM - 01:00 PM",
    "03:00 PM - 05:00 PM",
    "05:00 PM - 07:00 PM",
];

// Build the next 14 available dates
const buildDates = () => {
    const out = [];
    const now = new Date();
    for (let i = 1; i <= 14; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);
        out.push({
            iso: d.toISOString().slice(0, 10),
            day: d.getDate(),
            weekday: d.toLocaleDateString("en-IN", { weekday: "short" }),
            month: d.toLocaleDateString("en-IN", { month: "short" }),
            full: d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" }),
        });
    }
    return out;
};

export default function SiteVisitModal({ property, onClose, onBooked }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const dates = useMemo(buildDates, []);
    const [date, setDate] = useState(dates[0].iso);
    const [time, setTime] = useState(TIME_SLOTS[1]);
    const [name, setName]   = useState(user?.name || "");
    const [phone, setPhone] = useState(user?.phone || "");
    const [notes, setNotes] = useState("");
    const [busy, setBusy]   = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        // Guard: only buyers can book
        if (!user) {
            toast.info("Please sign in as a buyer to book a site visit");
            nav(`/login?role=buyer`);
            return;
        }
        if (user.role !== "buyer") {
            toast.error(`Site visits can only be booked from a buyer account (you're logged in as ${user.role}).`);
            return;
        }
        setBusy(true);
        try {
            // Persist to buyer's site-visit ledger
            await api.post("/buyer/site-visits", {
                property_id: property.id,
                property_title: property.title,
                preferred_date: date,
                preferred_time: time,
                visitor_name: name,
                visitor_phone: phone,
                notes,
            });
            // Also create a lead so admin / partner can see it in leads pipeline
            await api.post("/leads", {
                name: name || user.name || "Site Visit",
                email: user.email,
                phone: phone || user.phone || "+91-0000000000",
                source: "site_visit",
                property_id: property.id,
                property_title: property.title,
                message: `SITE VISIT — ${property.title} · ${date} · ${time}${notes ? " · " + notes : ""}`,
            });
            toast.success("Site visit booked! Our advisor will confirm within a business day.");
            onBooked?.();
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-0 md:p-6" data-testid="site-visit-modal">
            <div className="bg-white w-full md:max-w-2xl md:max-h-[92vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <header className="flex items-center justify-between px-6 py-4 border-b border-black/10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Book Site Visit</div>
                        <h2 className="font-serif text-2xl mt-1">{property?.title}</h2>
                        <div className="text-black/55 text-[12px]">{property?.locality}, {property?.city}</div>
                    </div>
                    <button onClick={onClose} data-testid="site-visit-close" className="w-9 h-9 rounded-full border border-black/15 hover:bg-black hover:text-white flex items-center justify-center">
                        <X size={15}/>
                    </button>
                </header>

                {/* Assurance strip */}
                <div className="px-6 py-3 bg-[var(--ebg-ivory)] border-b border-black/8 flex items-center gap-2 text-[11px] text-black/70">
                    <ShieldCheck size={13} className="text-[var(--ebg-gold)]"/>
                    <span>Free · No obligation · EBG advisor accompanies you · Legal verified property</span>
                </div>

                <form onSubmit={submit} className="p-6 space-y-6">
                    {/* Date picker */}
                    <div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">
                            <Calendar size={12} className="text-[var(--ebg-gold)]"/> Choose Date
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="visit-date-grid">
                            {dates.map((d) => (
                                <button
                                    type="button"
                                    key={d.iso}
                                    onClick={() => setDate(d.iso)}
                                    data-testid={`visit-date-${d.iso}`}
                                    className={`p-2 border text-center transition-all ${date === d.iso ? "border-black bg-black text-white" : "border-black/12 hover:border-black"}`}
                                >
                                    <div className="text-[9px] uppercase tracking-[0.16em] opacity-70">{d.weekday}</div>
                                    <div className="font-serif text-xl mt-0.5">{d.day}</div>
                                    <div className="text-[9px] opacity-60">{d.month}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Time slots */}
                    <div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">
                            <Clock size={12} className="text-[var(--ebg-gold)]"/> Choose Slot
                        </div>
                        <div className="grid grid-cols-2 gap-2" data-testid="visit-time-grid">
                            {TIME_SLOTS.map((t) => (
                                <button
                                    type="button"
                                    key={t}
                                    onClick={() => setTime(t)}
                                    data-testid={`visit-time-${t.replace(/[^a-z0-9]/gi, '').toLowerCase()}`}
                                    className={`p-3 border text-[12px] font-bold transition-all ${time === t ? "border-[var(--ebg-gold)] bg-[var(--ebg-gold)] text-black" : "border-black/12 hover:border-black"}`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="grid md:grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5 flex items-center gap-1.5"><User size={11}/> Your Name</div>
                            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="visit-name" className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                        </label>
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5 flex items-center gap-1.5"><Phone size={11}/> Phone</div>
                            <input required value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="visit-phone" placeholder="+91 " className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                        </label>
                    </div>
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5 flex items-center gap-1.5"><MessageSquare size={11}/> Notes (optional)</div>
                        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="visit-notes" placeholder="Anything we should know before the visit?" className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                    </label>

                    {/* Summary + submit */}
                    <div className="bg-[var(--ebg-ivory)] border border-black/10 px-4 py-3 flex items-center justify-between">
                        <div>
                            <div className="text-[9px] uppercase tracking-[0.24em] font-bold text-black/50">Your slot</div>
                            <div className="font-serif text-lg">{dates.find((d) => d.iso === date)?.full} · {time}</div>
                        </div>
                        <ShieldCheck size={22} className="text-[var(--ebg-gold)]"/>
                    </div>

                    <button
                        disabled={busy || !name || !phone}
                        type="submit"
                        data-testid="visit-submit"
                        className="w-full bg-[var(--ebg-gold)] text-black py-4 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    >
                        {busy ? "Booking…" : (<>Confirm Site Visit <ArrowRight size={13}/></>)}
                    </button>
                    {!user && (
                        <div className="text-center text-[12px] text-black/60">
                            You&rsquo;ll be asked to sign in as a buyer to confirm this booking.
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
